import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  MapPin,
  Star,
  Calendar,
  Edit,
  Trash2,
  BookPlus,
  Plane,
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertCircle,
  ExternalLink,
} from "lucide-react";
import { Button } from "@tabipla/ui/components/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@tabipla/ui/components/alert-dialog";
import { toast } from "sonner";
import { MekkeLayout } from "../layouts/MekkeLayout";
import { useSpot, useDeleteSpot } from "../../application/hooks/useSpots";
import { getCategoryById } from "../../config/categories";
import { AddToTripDialog } from "../components/spots/AddToTripDialog";
import { getCategoryGradient } from "../../utils/style-config";

export function SpotDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: spot, isLoading, error } = useSpot(id);
  const deleteMutation = useDeleteSpot();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAddToTripOpen, setIsAddToTripOpen] = useState(false);

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteMutation.mutateAsync(id);
      toast.success(t("spots.deleted"));
      navigate("/");
    } catch {
      toast.error(t("common.error"));
    }
  };

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString("ja-JP", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (isLoading) {
    return (
      <MekkeLayout>
        <div className="flex min-h-screen items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </MekkeLayout>
    );
  }

  if (error || !spot) {
    return (
      <MekkeLayout>
        <div className="flex min-h-screen flex-col items-center justify-center px-4">
          <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
            <AlertCircle className="h-10 w-10 text-destructive" />
          </div>
          <h2 className="mb-2 text-lg font-semibold">{t("common.error")}</h2>
          <p className="mb-6 text-center text-sm text-muted-foreground">
            {t("error.failedToFetch")}
          </p>
          <Button variant="outline" onClick={() => navigate("/")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("common.back")}
          </Button>
        </div>
      </MekkeLayout>
    );
  }

  const category = getCategoryById(spot.category);
  const CategoryIcon = category.icon;
  const hasImages = spot.images.length > 0;
  const gradientConfig = getCategoryGradient(spot.category);

  return (
    <MekkeLayout
      fabAction={{
        icon: Edit,
        label: t("spots.edit"),
        onClick: () => navigate(`/spots/${id}/edit`),
      }}
    >
      <div className="min-h-screen pb-24">
        <AlertDialog>
          <div className="mx-auto max-w-4xl">
            {/* Cover Area with Header Buttons */}
            <div className="relative h-48 overflow-hidden sm:h-64">
              {hasImages ? (
                <>
                  <img
                    src={spot.images[currentImageIndex].url}
                    alt={spot.images[currentImageIndex].caption || spot.name}
                    className="h-full w-full object-cover"
                  />
                  {spot.images.length > 1 && (
                    <>
                      <button
                        onClick={() =>
                          setCurrentImageIndex((prev) =>
                            prev > 0 ? prev - 1 : spot.images.length - 1
                          )
                        }
                        className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-black/70"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() =>
                          setCurrentImageIndex((prev) =>
                            prev < spot.images.length - 1 ? prev + 1 : 0
                          )
                        }
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-black/70"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                      <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">
                        {spot.images.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setCurrentImageIndex(index)}
                            className={`h-2 w-2 rounded-full transition-colors ${
                              index === currentImageIndex ? "bg-white" : "bg-white/50"
                            }`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div
                  className="relative h-full w-full"
                  style={{ background: gradientConfig.gradient }}
                >
                  {/* Decorative circles */}
                  <div
                    className="absolute -right-8 -top-8 h-32 w-32 rounded-full"
                    style={{ backgroundColor: gradientConfig.decor1 }}
                  />
                  <div
                    className="absolute -bottom-4 -left-4 h-24 w-24 rounded-full"
                    style={{ backgroundColor: gradientConfig.decor2 }}
                  />
                  {/* Category icon */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <CategoryIcon className="h-16 w-16 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]" />
                  </div>
                </div>
              )}

              {/* Header buttons overlaid on cover */}
              <div className="absolute inset-x-0 top-0 flex items-center justify-between px-3 py-3">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(-1)}
                  className="h-9 w-9 rounded-full bg-black/30 text-white backdrop-blur-sm hover:bg-black/40"
                >
                  <ArrowLeft className="h-5 w-5" />
                  <span className="sr-only">{t("common.back")}</span>
                </Button>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 rounded-full bg-black/30 text-white backdrop-blur-sm hover:bg-black/40"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="sr-only">{t("spots.delete")}</span>
                  </Button>
                </AlertDialogTrigger>
              </div>

              {/* Status badge */}
              <div
                className={`absolute bottom-3 left-3 flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium text-white shadow-lg backdrop-blur-sm ${
                  spot.status === "visited" ? "bg-indigo-600" : "bg-amber-600"
                }`}
              >
                {t(`spots.${spot.status === "visited" ? "visited" : "wantToGo"}`)}
              </div>
            </div>

            {/* Content */}
            <div className="px-4 pt-6">
              {/* Title */}
              <h1 className="text-2xl font-bold">{spot.name}</h1>
              {spot.address && (
                <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4 shrink-0" />
                  {spot.address}
                </p>
              )}

              {/* Meta info */}
              <div className="mt-4 flex flex-wrap items-center gap-3">
                {/* Category */}
                <div
                  className="flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium"
                  style={{
                    backgroundColor: `${category.color}15`,
                    color: category.color,
                  }}
                >
                  <CategoryIcon className="h-4 w-4" />
                  {t(category.labelKey)}
                </div>

                {/* Rating */}
                {spot.rating && (
                  <div className="flex items-center gap-1 text-sm">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`h-4 w-4 ${
                          star <= spot.rating!
                            ? "fill-amber-400 text-amber-400"
                            : "text-muted-foreground/30"
                        }`}
                      />
                    ))}
                  </div>
                )}

                {/* Visit date */}
                {spot.visitedAt && (
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    {formatDate(spot.visitedAt)}
                  </div>
                )}
              </div>

              {/* Description */}
              {spot.description && (
                <p className="mt-4 whitespace-pre-wrap text-muted-foreground">{spot.description}</p>
              )}

              {/* Tags */}
              {spot.tags.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {spot.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Map Link */}
              {spot.latitude && spot.longitude && (
                <div className="mt-6 overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                  <div className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-200">
                        <MapPin className="h-5 w-5 text-slate-600" />
                      </div>
                      <span className="font-medium">{t("spots.viewOnMap")}</span>
                    </div>
                    <Button size="sm" asChild>
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${spot.latitude},${spot.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="mr-2 h-4 w-4" />
                        {t("spots.openInMaps")}
                      </a>
                    </Button>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="mt-4 flex flex-col gap-2">
                <Button className="w-full gap-2" variant="outline" size="lg">
                  <BookPlus className="h-5 w-5" />
                  {t("spots.addToBooklet")}
                </Button>
                <Button
                  className="w-full gap-2 border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700"
                  variant="outline"
                  size="lg"
                  onClick={() => setIsAddToTripOpen(true)}
                >
                  <Plane className="h-5 w-5" />
                  {t("spots.addToTabitto")}
                </Button>
              </div>
            </div>
          </div>

          {/* Add to Trip Dialog */}
          <AddToTripDialog
            spotId={spot.id}
            spotName={spot.name}
            open={isAddToTripOpen}
            onOpenChange={setIsAddToTripOpen}
          />

          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("spots.delete")}</AlertDialogTitle>
              <AlertDialogDescription>{t("spots.deleteConfirm")}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {t("common.delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </MekkeLayout>
  );
}
